import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import { TestCard } from "../../components/global/VideoCard";
import { TestContext } from "../../context/test_context";
import { debounce } from "lodash";
import useAuth from "../../hooks/auth_hook";
import { useNavigate } from "react-router-dom";
import LoadBox from "../../components/global/LoadBox";
import moment from "moment";
import { RefreshCw } from "lucide-react";
import TestsControlButtons from "../../components/tests/TestsControlButtons";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";

export const TestPageContext = createContext<any>(null);

export default function Tests(): any {
  const [reload, setReload] = useState(false);
  return (
    <TestPageContext.Provider value={{ reload, setReload }}>
      <div className="w-full flex flex-col md:flex-row justify-center mx-5 pt-4 max-h-full">
        <TestList finished={false} active={true} title={"Ongoing Tests"} />
        <TestList finished={false} active={false} title={"Paused Tests"} />
      </div>
    </TestPageContext.Provider>
  );
}

export type TestList = {
  tests: any;
  testgroup: any | null;
  testgroup_index: any;
};

export function TestGroupBox({ tests, testgroup, testgroup_index }: TestList): any {
  const navigate = useNavigate();
  const { loading, pauseLoading } = React.useContext(TestContext);

  const calculateCurrentTest = (tests: any) => {
    if (tests.filter((test: any) => test.tested).length === tests.length) {
      return tests[tests.length - 1];
    }
    return tests.find((test: any) => test.active) || tests[0];
  };

  const currentTest = calculateCurrentTest(tests);
  const { user } = useAuth();

  let test_status = "";
  if (currentTest && tests) {
    if (currentTest.order + 1 < tests.length) {
      test_status = `On test ${currentTest.order + 1} out of ${tests.length}`;
    } else if (currentTest.order + 1 === tests.length) {
      test_status = `Test Complete`;
    }
  }

  const firstRender = useRef(true);
  useEffect(() => {
    setTimeout(() => {
      firstRender.current = false;
    }, 0);
  }, []);

  const isActive = testgroup.active;
  const isFinished = testgroup.finished;

  return (
    currentTest &&
    testgroup && (
      <div className="w-full h-full p-4 overflow-y-visible relative" id={currentTest.id}>
        <div
          className={cn(
            "absolute top-4 left-4 w-2.5 h-2.5 rounded-full z-10",
            isActive ? "bg-success animate-pulse" : isFinished ? "bg-success" : "bg-warning"
          )}
          style={{ backgroundColor: isActive || isFinished ? "#66bb6a" : "#ffb74d" }}
        />

        <div className="w-full h-full bg-card border border-border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
          <LoadBox loading={(pauseLoading || loading) && firstRender.current}>
            <div
              className="cursor-pointer flex-1 flex flex-col"
              onClick={() => {
                if (currentTest && currentTest.testgroup) {
                  navigate(`/tests/${currentTest.testgroup}`);
                }
              }}
            >
              <div className="relative w-full overflow-hidden bg-black">
                <img
                  src={currentTest?.imgLink ?? ""}
                  className="w-full object-contain min-h-[90px] min-w-[160px] max-h-[300px]"
                  alt={`Thumbnail for Youtube Video titled: ${currentTest.title || ""}`}
                />
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="flex items-start gap-2 mb-2">
                  {user?.picture && (
                    <img
                      src={user.picture}
                      alt="User avatar"
                      className="h-8 w-8 rounded-full shrink-0"
                    />
                  )}
                  <div className="flex-1">
                    <h6 className="text-sm font-medium line-clamp-2">{currentTest?.title ?? ""}</h6>
                    <div className="text-xs text-muted-foreground mt-1">
                      <span>
                        {test_status}
                        <br />
                        {currentTest?.time_to_test
                          ? moment(currentTest.time_to_test).fromNow()
                          : ""}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-full justify-center py-2">
              <TestsControlButtons testgroup={testgroup} testgroup_index={testgroup_index} />
            </div>
          </LoadBox>
        </div>
      </div>
    )
  );
}

export const TestListContext = createContext<any>(null);

export function TestList({ finished, active, title }: any): any {
  const { loading, getTestGroups, results_per_page } = React.useContext(TestContext);

  const [testgroups, setTestGroups] = useState<any>([]);
  const [search, setSearch] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [count, setCount] = useState<number>(0);
  const totalPages = Math.ceil(count / results_per_page);
  const { reload, setReload } = useContext(TestPageContext);

  const searchTestGroups = async (search: string, currentPage: number) => {
    getTestGroups(search, currentPage, { finished, active }).then(
      ({ testgroups, count }: any) => {
        setTestGroups(testgroups);
        setCount(count);
      }
    );
  };

  const debouncedSearch = debounce(searchTestGroups, 500);
  useEffect(() => {
    debouncedSearch(search, currentPage);
  }, [search, currentPage]);

  const filteredTestGroups = React.useMemo(
    () =>
      testgroups.filter((testgroup: any) => {
        return (
          testgroup?.tests?.some((test: any) =>
            test?.title?.toLowerCase().includes(search.toLowerCase())
          )
        );
      }),
    [search, testgroups]
  );

  useEffect(() => {
    searchTestGroups(search, currentPage);
  }, [reload]);

  const firstRender = useRef(true);
  useEffect(() => {
    setTimeout(() => {
      firstRender.current = false;
    }, 0);
  }, []);

  return (
    <TestListContext.Provider value={{ testgroups, setTestGroups }}>
      <div className="w-full h-full overflow-y-visible mb-4">
        <div className="m-2 p-4 bg-card border border-border rounded-lg shadow-sm">
          <h3 className="text-2xl font-semibold mb-4">{title}</h3>
          <div className="flex justify-between items-center gap-2 mb-4">
            <input
              className="flex-1 px-3 py-2 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              value={search}
              placeholder="Search..."
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => searchTestGroups(search, currentPage)}
              aria-label="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <LoadBox loading={firstRender.current}>
            <div className="flex justify-start items-start flex-wrap gap-4">
              {filteredTestGroups.map((testgroup: any, ind: number) => (
                <TestGroupBox
                  tests={testgroup.tests}
                  key={ind}
                  testgroup={testgroup}
                  testgroup_index={ind}
                />
              ))}
              {filteredTestGroups.length === 0 && (
                <div className="flex justify-center items-center h-full w-full p-8">
                  <p className="text-base text-muted-foreground">No tests...</p>
                </div>
              )}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4 mb-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                ))}
              </div>
            )}
          </LoadBox>
        </div>
      </div>
    </TestListContext.Provider>
  );
}
